// #region Imports
import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchAirlinePerformanceReport,
    resetAirlinePerformanceReport,
    selectAirlinePerformanceData,
    selectAirlinePerformanceStatus,
    selectAirlinePerformanceError,
} from '_redux/slices/sla-reports/airline-performance';
import { Row, Col } from 'react-bootstrap';
import ReportHeader from './report-header';
import AirlinePerformanceCombinedTable from './airline-performance-combined-table';
import AirlinePerformancePreadvisedTable from './airline-performance-preadvised-table';
import AirlinePerformanceVolumeTable from './airline-performance-volume-table';
import AirlinePerformancePreadvisedRawTable from './airline-performance-preadvised-raw-table';
import { sanitizeSpreadsheetCell, sanitizeExcelWorkbook } from '_utils/sanitize-spreadsheet-cell';
// #endregion

/**
 * Airline Performance Report - Displays airline performance metrics for the selected date range and IATA.
 * @param {Object} props
 * @param {Array} props.dateRange - [startDate, endDate] for the report
 * @param {string} props.iata - IATA code to filter by
 */
const AirlinePerformanceReport = ({ dateRange, iata, runReportRef, onRunLoadingChange, hideBreakdown }) => {
    const dispatch = useDispatch();
    const data = useSelector(selectAirlinePerformanceData);
    const status = useSelector(selectAirlinePerformanceStatus);
    const error = useSelector(selectAirlinePerformanceError);
    const isLoading = status === 'loading';

    const handleRunReport = useCallback(() => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];

        if (!startDate || !endDate || !iata) return;

        const toLocalDateString = (d) => {
            const date = new Date(d);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);

        dispatch(
            fetchAirlinePerformanceReport({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                iata,
                hideBreakdown,
            })
        );
    }, [dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        return () => {
            dispatch(resetAirlinePerformanceReport());
        };
    }, [dispatch]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);

    return (
        <div>
            <ReportHeader onRunReport={handleRunReport} isLoading={isLoading} hideRunButton={!!runReportRef} />

            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>
                    Error loading Airline Performance report: {error}
                </p>
            )}

            {data?.reports?.preadvisedRanking && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
                        <button
                            type="button"
                            onClick={() => {
                                const esc = (v) => `"${String(sanitizeSpreadsheetCell(v ?? '')).replace(/"/g, '""')}"`;
                                const lines = [];
                                lines.push(esc('Airline Performance (Combined Ranking)'));
                                lines.push(['Airline', 'Pre-Advise Rank', 'PRM Volume Rank', 'Total', 'Rank'].map(esc).join(','));
                                (data.reports.combinedRanking?.airlines || []).forEach((a) => {
                                    lines.push([a.airline, a.preAdviseRank, a.prmVolumeRank, a.total, a.rank].map(esc).join(','));
                                });
                                lines.push('');
                                lines.push(esc('Pre-Advised Rank'));
                                lines.push(['Rank', 'Airline', 'Total Pre-Advise', 'Total Non-Advise', 'Total Asap', 'Pre-Advise %', 'Non-Advise %', 'Total', 'Total %'].map(esc).join(','));
                                (data.reports.preadvisedRanking?.airlines || []).forEach((a) => {
                                    lines.push([a.rank, a.airline, a.totalPreAdvise, a.totalNonAdvise, a.totalAsap, `${a.preAdvisePct}%`, `${a.nonAdvisePct}%`, a.total, `${a.totalPct}%`].map(esc).join(','));
                                });
                                if (data.reports.preadvisedRanking?.totals) {
                                    const t = data.reports.preadvisedRanking.totals;
                                    lines.push(['', 'Total', t.totalPreAdvise, t.totalNonAdvise, t.totalAsap, `${t.preAdvisePct}%`, `${t.nonAdvisePct}%`, t.total, `${t.totalPct}%`].map(esc).join(','));
                                }
                                lines.push('');
                                lines.push(esc('PRM Volume Ranking'));
                                lines.push(['Rank', 'Airline', 'Total Pre-Advise', 'Total Non-Advise', 'Total Asap', 'Pre-Advise %', 'Non-Advise %', 'Total', 'Total %'].map(esc).join(','));
                                (data.reports.volumeRanking?.airlines || []).forEach((a) => {
                                    lines.push([a.rank, a.airline, a.totalPreAdvise, a.totalNonAdvise, a.totalAsap, `${a.preAdvisePct}%`, `${a.nonAdvisePct}%`, a.total, `${a.totalPct}%`].map(esc).join(','));
                                });
                                if (data.reports.volumeRanking?.totals) {
                                    const t = data.reports.volumeRanking.totals;
                                    lines.push(['', 'Total', t.totalPreAdvise, t.totalNonAdvise, t.totalAsap, `${t.preAdvisePct}%`, `${t.nonAdvisePct}%`, t.total, `${t.totalPct}%`].map(esc).join(','));
                                }
                                const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                                const link = document.createElement('a');
                                link.href = URL.createObjectURL(blob);
                                link.download = `airline-performance-${new Date().toISOString().slice(0, 10)}.csv`;
                                link.click();
                                URL.revokeObjectURL(link.href);
                            }}
                            style={{
                                padding: '6px 14px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                backgroundColor: '#f3f4f6',
                                color: '#374151',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                            }}
                        >
                            Export All Tables CSV
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                const Excel = await import('exceljs/dist/exceljs.min.js');
                                const workbook = new Excel.Workbook();
                                const COLORS = {
                                    titleBg: 'FF4B5563',
                                    headerBg: 'FF6B7280',
                                    lightHeaderBg: 'FFF9FAFB',
                                    totalsBg: 'FFE5E7EB',
                                    yellowBg: 'FFFEF9C3',
                                    whiteText: 'FFFFFFFF',
                                    darkText: 'FF374151',
                                };
                                const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                                const applyCell = (cell, { fill, font, align = 'center' } = {}) => {
                                    cell.border = border;
                                    cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
                                    if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
                                    if (font) cell.font = font;
                                };

                                // ── Sheet 1: Combined Ranking ──────────────────────────
                                const s1 = workbook.addWorksheet('Combined Ranking');
                                s1.getColumn(1).width = 28; s1.getColumn(2).width = 16; s1.getColumn(3).width = 18; s1.getColumn(4).width = 10; s1.getColumn(5).width = 10;
                                const s1Title = s1.addRow(['Airline Performance', '', '', '', '']);
                                s1.mergeCells(`A${s1Title.number}:E${s1Title.number}`);
                                applyCell(s1Title.getCell(1), { fill: COLORS.titleBg, font: { bold: true, color: { argb: COLORS.whiteText }, size: 13 } });
                                s1Title.height = 26;
                                const s1Hdr = s1.addRow(['Airline', 'Pre-Advise Rank', 'PRM Volume Rank', 'Total', 'Rank']);
                                ['left', 'center', 'center', 'center', 'center'].forEach((align, i) => {
                                    applyCell(s1Hdr.getCell(i + 1), { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.whiteText }, size: 12 }, align });
                                });
                                s1Hdr.height = 24;
                                (data.reports.combinedRanking?.airlines || []).forEach((a) => {
                                    const row = s1.addRow([a.airline, a.preAdviseRank, a.prmVolumeRank, a.total, a.rank]);
                                    applyCell(row.getCell(1), { align: 'left' });
                                    [2, 3, 4].forEach(c => applyCell(row.getCell(c)));
                                    applyCell(row.getCell(5), { fill: COLORS.yellowBg });
                                    row.height = 22;
                                });

                                // ── Sheet 2: Pre-Advised Rank ──────────────────────────
                                const s2 = workbook.addWorksheet('Pre-Advised Rank');
                                [10, 28, 16, 16, 12, 14, 14, 10, 14].forEach((w, i) => { s2.getColumn(i + 1).width = w; });
                                const s2Title = s2.addRow(['Pre-Advised Rank', '', '', '', '', '', '', '', '']);
                                s2.mergeCells(`A${s2Title.number}:I${s2Title.number}`);
                                applyCell(s2Title.getCell(1), { fill: COLORS.titleBg, font: { bold: true, color: { argb: COLORS.whiteText }, size: 13 } });
                                s2Title.height = 26;
                                const s2Hdr = s2.addRow(['Rank', 'Airline', 'Total Pre-Advise', 'Total Non-Advise', 'Total Asap', 'Pre-Advise %', 'Non-Advise %', 'Total', 'Total %']);
                                ['center', 'left', 'center', 'center', 'center', 'center', 'center', 'center', 'center'].forEach((align, i) => {
                                    applyCell(s2Hdr.getCell(i + 1), { fill: COLORS.lightHeaderBg, font: { bold: true, color: { argb: COLORS.darkText }, size: 12 }, align });
                                });
                                s2Hdr.height = 24;
                                (data.reports.preadvisedRanking?.airlines || []).forEach((a) => {
                                    const row = s2.addRow([a.rank, a.airline, a.totalPreAdvise, a.totalNonAdvise, a.totalAsap, `${a.preAdvisePct}%`, `${a.nonAdvisePct}%`, a.total, `${a.totalPct}%`]);
                                    applyCell(row.getCell(1));
                                    applyCell(row.getCell(2), { align: 'left' });
                                    [3, 4, 5, 7, 8].forEach(c => applyCell(row.getCell(c)));
                                    applyCell(row.getCell(6), { fill: COLORS.yellowBg });
                                    applyCell(row.getCell(9));
                                    row.height = 22;
                                });
                                if (data.reports.preadvisedRanking?.totals) {
                                    const t = data.reports.preadvisedRanking.totals;
                                    const row = s2.addRow(['', 'Total', t.totalPreAdvise, t.totalNonAdvise, t.totalAsap, `${t.preAdvisePct}%`, `${t.nonAdvisePct}%`, t.total, `${t.totalPct}%`]);
                                    [1, 2, 3, 4, 5, 6, 7, 8, 9].forEach(c => applyCell(row.getCell(c), { fill: COLORS.totalsBg, font: { bold: true }, align: c === 2 ? 'left' : 'center' }));
                                    row.height = 22;
                                }

                                // ── Sheet 3: PRM Volume Ranking ────────────────────────
                                const s3 = workbook.addWorksheet('PRM Volume Ranking');
                                [10, 28, 16, 16, 12, 14, 14, 10, 14].forEach((w, i) => { s3.getColumn(i + 1).width = w; });
                                const s3Title = s3.addRow(['PRM Volume Ranking', '', '', '', '', '', '', '', '']);
                                s3.mergeCells(`A${s3Title.number}:I${s3Title.number}`);
                                applyCell(s3Title.getCell(1), { fill: COLORS.titleBg, font: { bold: true, color: { argb: COLORS.whiteText }, size: 13 } });
                                s3Title.height = 26;
                                const s3Hdr = s3.addRow(['Rank', 'Airline', 'Total Pre-Advise', 'Total Non-Advise', 'Total Asap', 'Pre-Advise %', 'Non-Advise %', 'Total', 'Total %']);
                                ['center', 'left', 'center', 'center', 'center', 'center', 'center', 'center', 'center'].forEach((align, i) => {
                                    applyCell(s3Hdr.getCell(i + 1), { fill: COLORS.lightHeaderBg, font: { bold: true, color: { argb: COLORS.darkText }, size: 12 }, align });
                                });
                                s3Hdr.height = 24;
                                (data.reports.volumeRanking?.airlines || []).forEach((a) => {
                                    const row = s3.addRow([a.rank, a.airline, a.totalPreAdvise, a.totalNonAdvise, a.totalAsap, `${a.preAdvisePct}%`, `${a.nonAdvisePct}%`, a.total, `${a.totalPct}%`]);
                                    applyCell(row.getCell(1));
                                    applyCell(row.getCell(2), { align: 'left' });
                                    [3, 4, 5, 6, 7, 8].forEach(c => applyCell(row.getCell(c)));
                                    applyCell(row.getCell(9), { fill: COLORS.yellowBg });
                                    row.height = 22;
                                });
                                if (data.reports.volumeRanking?.totals) {
                                    const t = data.reports.volumeRanking.totals;
                                    const row = s3.addRow(['', 'Total', t.totalPreAdvise, t.totalNonAdvise, t.totalAsap, `${t.preAdvisePct}%`, `${t.nonAdvisePct}%`, t.total, `${t.totalPct}%`]);
                                    [1, 2, 3, 4, 5, 6, 7, 8, 9].forEach(c => applyCell(row.getCell(c), { fill: COLORS.totalsBg, font: { bold: true }, align: c === 2 ? 'left' : 'center' }));
                                    row.height = 22;
                                }

                                sanitizeExcelWorkbook(workbook);
                                const buf = await workbook.xlsx.writeBuffer();
                                const link = document.createElement('a');
                                link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                                link.download = `airline-performance-${new Date().toISOString().slice(0, 10)}.xlsx`;
                                link.click();
                                URL.revokeObjectURL(link.href);
                            }}
                            style={{
                                padding: '6px 14px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                border: '1px solid #bbf7d0',
                                borderRadius: '6px',
                            }}
                        >
                            Export All Tables Excel
                        </button>
                    </div>
                    <Row>
                        <Col xs={12} md={4} className="mb-3 mb-md-0">
                            <AirlinePerformanceCombinedTable airlines={data.reports.combinedRanking?.airlines} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6} className="mb-3 mb-md-0">
                            <AirlinePerformancePreadvisedTable
                                airlines={data.reports.preadvisedRanking.airlines}
                                totals={data.reports.preadvisedRanking.totals}
                            />
                        </Col>
                        <Col md={6}>
                            <AirlinePerformanceVolumeTable
                                airlines={data.reports.volumeRanking?.airlines}
                                totals={data.reports.volumeRanking?.totals}
                            />
                        </Col>
                    </Row>
                    {!hideBreakdown && (
                        <div style={{ marginTop: '32px' }}>
                            <AirlinePerformancePreadvisedRawTable rawData={data.reports.preadvisedRanking.rawData} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AirlinePerformanceReport;
